import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/common/ImageUpload";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, PageHeader } from "@/components/common/Primitives";

const Card = ({ title, desc, children, onSave, busy, testId }) => (
  <form onSubmit={(e) => { e.preventDefault(); onSave(); }} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={testId}>
    <h2 className="font-heading text-lg font-semibold text-slate-900">{title}</h2>
    {desc && <p className="mb-5 mt-1 text-sm text-slate-500">{desc}</p>}
    <div className="space-y-4">{children}</div>
    <Button type="submit" disabled={busy} className="mt-5 bg-emerald-500 hover:bg-emerald-600" data-testid={`${testId}-save`}>
      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
    </Button>
  </form>
);
const L = ({ label, children }) => <div><label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>{children}</div>;

function gstPreview(g) {
  const price = 100, rate = Number(g.rate) || 0;
  if (!g.enabled || !rate) return `Item ₹100 → customer pays ${money(100)} (no GST)`;
  if (g.inclusive) return `Item ₹100 includes ${money((price * rate) / (100 + rate))} GST → customer pays ${money(100)}`;
  return `Item ₹100 + ${money(rate)} GST → customer pays ${money(100 + rate)}`;
}

export default function Settings() {
  const { setRestaurant } = useAuth();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/restaurant/profile").then((x) => setR(x.data)); }, []);
  if (!r) return <Loader />;

  const set = (k) => (e) => setR({ ...r, [k]: e?.target ? e.target.value : e });
  const gst = r.gst || {};
  const wa = r.whatsapp || {};
  const save = async (key, path, payload) => {
    setBusy(key);
    try { const { data } = await api.patch(path, payload); setR(data); setRestaurant(data); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const saveLogo = async (url) => {
    try {
      const { data } = await api.patch("/restaurant/logo", { logo_url: url });
      setR((prev) => ({ ...prev, logo_url: data.logo_url }));
      setRestaurant(data);
      toast.success(url ? "Logo saved" : "Logo removed");
    } catch (e) { toast.error(apiError(e, "Could not save the logo. Please try again.")); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle={`Public menu: ${window.location.origin}/r/${r.slug}`} />
      <Card title="Restaurant profile" testId="profile-card" busy={busy === "p"}
        onSave={() => save("p", "/restaurant/profile", { name: r.name, owner_name: r.owner_name || "", phone: r.phone || "", address: r.address || "", cuisine: r.cuisine || "", description: r.description || "", logo_url: r.logo_url || "", gstin: r.gstin || "" })}>
        <ImageUpload value={r.logo_url} onChange={saveLogo} label="Logo" testId="logo-upload" quiet />
        <div className="grid gap-4 sm:grid-cols-2">
          <L label="Restaurant name"><Input required value={r.name} onChange={set("name")} data-testid="profile-name" /></L>
          <L label="Owner name"><Input value={r.owner_name || ""} onChange={set("owner_name")} data-testid="profile-owner" /></L>
          <L label="Phone"><Input value={r.phone || ""} onChange={set("phone")} data-testid="profile-phone" /></L>
          <L label="Cuisine"><Input value={r.cuisine || ""} onChange={set("cuisine")} data-testid="profile-cuisine" /></L>
          <L label="GSTIN (optional)"><Input value={r.gstin || ""} onChange={set("gstin")} data-testid="profile-gstin" /></L>
        </div>
        <L label="Address"><Textarea rows={2} value={r.address || ""} onChange={set("address")} data-testid="profile-address" /></L>
        <L label="Short description"><Textarea rows={2} value={r.description || ""} onChange={set("description")} data-testid="profile-description" /></L>
      </Card>

      <Card title="Tax & GST" desc="GST is optional. Item-level rules can override these defaults." testId="gst-card" busy={busy === "g"}
        onSave={() => save("g", "/restaurant/gst", { enabled: !!gst.enabled, rate: Number(gst.rate) || 0, inclusive: !!gst.inclusive, show_on_invoice: gst.show_on_invoice !== false })}>
        <label className="flex items-center gap-3 text-sm"><Switch checked={!!gst.enabled} onCheckedChange={(v) => setR({ ...r, gst: { ...gst, enabled: v } })} data-testid="gst-enabled" />Charge GST</label>
        {gst.enabled && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <L label="Default GST rate (%)"><Input type="number" min="0" max="100" step="0.01" value={gst.rate ?? 5} onChange={(e) => setR({ ...r, gst: { ...gst, rate: e.target.value } })} data-testid="gst-rate" /></L>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[[false, "GST added on top"], [true, "Prices include GST"]].map(([v, l]) => (
                <button type="button" key={l} onClick={() => setR({ ...r, gst: { ...gst, inclusive: v } })} data-testid={`gst-mode-${v ? "inclusive" : "exclusive"}`}
                  className={`rounded-xl border px-3 py-2 text-sm transition-colors ${!!gst.inclusive === v ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200"}`}>{l}</button>
              ))}
            </div>
            <label className="flex items-center gap-3 text-sm"><Switch checked={gst.show_on_invoice !== false} onCheckedChange={(v) => setR({ ...r, gst: { ...gst, show_on_invoice: v } })} data-testid="gst-show-invoice" />Show GST breakdown on invoice</label>
          </>
        )}
        <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="gst-preview">{gstPreview(gst)}</p>
      </Card>

      <Card title="WhatsApp" desc="Customers and staff can share orders via WhatsApp (opens WhatsApp with a pre-filled message). Automated messaging needs a WhatsApp Business API provider and is not enabled." testId="whatsapp-card" busy={busy === "w"}
        onSave={() => save("w", "/restaurant/whatsapp", { number: wa.number || "", enabled: !!wa.enabled })}>
        <L label="WhatsApp number (with country code)"><Input value={wa.number || ""} onChange={(e) => setR({ ...r, whatsapp: { ...wa, number: e.target.value } })} placeholder="919876543210" data-testid="whatsapp-number" /></L>
        <label className="flex items-center gap-3 text-sm"><Switch checked={!!wa.enabled} onCheckedChange={(v) => setR({ ...r, whatsapp: { ...wa, enabled: v } })} data-testid="whatsapp-enabled" />Show "Send order on WhatsApp" to customers</label>
      </Card>
    </div>
  );
}
